import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import ErpTemplate from '../../components/ErpTemplate';
import { XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine, PieChart, Pie, Cell } from 'recharts';
import ReactSelect from '../../components/ReactSelect';
import { FaDownload, FaCalendarAlt, FaChartBar, FaChartPie, FaTable } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { FinField, FinInput } from '../../components/FinField';
import '../../css/financeiro-parcela-pagar.css';

const COLORS = ['#0F2344', '#1F51A8', '#2E73E9', '#5A9CF0', '#A0C4F8', '#C8DCFB'];
const PAGE_SIZE_DEFAULT = 20;
const RESUMO_INICIAL = {
  totalEventos: 0,
  clientesAtendidos: 0,
  responsaveisAtivos: 0,
  taxaConclusao: 0,
  eventosConcluidos: 0,
  eventosPorMes: [],
  distribuicaoPorCliente: [],
};

const STAT_CARDS = [
  { key: 'totalEventos', label: 'Total de Eventos', desc: 'no período', cor: '#2E73E9' },
  { key: 'clientesAtendidos', label: 'Clientes Atendidos', desc: 'clientes únicos', cor: '#1F51A8' },
  { key: 'responsaveisAtivos', label: 'Responsáveis Ativos', desc: 'cliente, agência, rsvp e diretor', cor: '#0F2344' },
];

export default function Graficos() {
  const token = localStorage.getItem('token');
  const empresa = localStorage.getItem("empresa");
  const [eventos, setEventos] = useState([]);
  const [resumo, setResumo] = useState(RESUMO_INICIAL);
  const [isLoadingResumo, setIsLoadingResumo] = useState(true);
  const [isLoadingTabela, setIsLoadingTabela] = useState(true);
  const pieBodyRef = useRef(null);
  const [barChartHeight, setBarChartHeight] = useState(200);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: PAGE_SIZE_DEFAULT,
    total: 0,
  });
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const [filtro, setFiltro] = useState({
    empresa: empresa,
  }); //1 - pagar | 2 - receber

  const [buscarInput, setBuscarInput] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setFiltro(prev => ({ ...prev, buscar: buscarInput || undefined }));
    }, 800);
    return () => clearTimeout(timer);
  }, [buscarInput]);

  const buscarResumo = () => {
    setIsLoadingResumo(true);
    api({
      method: "get",
      action: '/evento/graficosResumo',
      token: token,
      params: {
        ...filtro
      }
    }).then(response => {
      setResumo({
        ...RESUMO_INICIAL,
        ...(response.data.data || {}),
      });
    }).catch(err => {
      setResumo(RESUMO_INICIAL);
      toast.error("Erro: ao carregar resumo")
    }).finally(() => {
      setIsLoadingResumo(false);
    });
  };

  const buscarTabela = ({ page = pagination.page, pageSize = pagination.pageSize, resetPage = false } = {}) => {
    if (resetPage) {
      setResetPaginationToggle(valorAtual => !valorAtual);
    }

    setIsLoadingTabela(true);
    api({
      method: "get",
      action: '/evento',
      token: token,
      params: {
        ...filtro,
        page,
        pageSize,
      }
    }).then(response => {
      const lista = response.data.data || [];
      setEventos(lista);
      setPagination({
        page: response.data.page ?? page,
        pageSize: response.data.pageSize ?? pageSize,
        total: response.data.total ?? lista.length,
      });
    }).catch(err => {
      setEventos([]);
      setPagination(valorAtual => ({
        ...valorAtual,
        page,
        pageSize,
        total: 0,
      }));
      toast.error("Erro: ao carregar dados")
    }).finally(() => {
      setIsLoadingTabela(false);
    });
  };

  useEffect(() => {
    if (!pieBodyRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setBarChartHeight(entry.contentRect.height);
    });
    observer.observe(pieBodyRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { //onload
    buscarResumo();
    buscarTabela({ page: 1, resetPage: true });
  }, [filtro]);

  const eventosPorMes = resumo.eventosPorMes || [];
  const distribuicaoPorCliente = resumo.distribuicaoPorCliente || [];

  const max = Math.max(...eventosPorMes.map(d => d.total), 0);
  const step = Math.max(1, Math.ceil(max / 9));
  const ticks = [...Array(10)].map((_, i) => Math.round(step * i));

  const totalPaginas = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.pageSize || PAGE_SIZE_DEFAULT)));

  const tiposEvento = {
    advisoryboard: 'Advisory Board',
    congresso: 'Congresso',
    convencaonacional: 'Convenção',
    estandecotapat: 'Estande e/ou Cota de Patrocínio',
    eventoexclusivo: 'Evento Exclusivo',
    eventointerno: 'Evento Interno',
    eventoterceiros: 'Evento de Terceiros',
    minimeet: 'Mini Meeting',
    orgsede: 'Organizado Pela Sede',
    simposio: 'Simpósio',
  };

  const statusMap = {
    ativo: { label: 'Em andamento', bg: '#dbeafe', color: '#1d4ed8' },
    fechado: { label: 'Concluído', bg: '#dcfce7', color: '#15803d' },
    pendente: { label: 'Pendente', bg: '#fef9c3', color: '#a16207' },
    cancelado: { label: 'Cancelado', bg: '#fee2e2', color: '#b91c1c' },
  };

  const nomesResponsaveis = (row) => {
    const all = [
      row.operador,
      row.diretorresponsavel,
      row.coordenadorrsvp,
      row.responsavelcliente,
      ...(Array.isArray(row.responsavel) ? row.responsavel : []),
    ].filter(p => p?.id);
    return [...new Map(all.map(p => [p.id, p])).values()].map(p => p.nome).filter(Boolean).join(', ');
  };

  return (
    <ErpTemplate>
      <div className="main-content container-fluid fin-pp">
        <div className="fin-pp__head">
          <div>
            <h1 className="fin-pp__title">Gráficos</h1>
            <p className="fin-pp__subtitle">Visão geral dos eventos por período, cliente e responsável.</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
          <form className="form" onSubmit={(e) => { buscarResumo(); buscarTabela({ page: 1, resetPage: true }); e.preventDefault(); }}>
            <div className="fin-pp__filters">
              <FinField label="Período — De">
                <input
                  type="date"
                  className="fin-pp__input"
                  style={{ paddingLeft: 14 }}
                  value={filtro.dataInicial || ''}
                  onChange={e => setFiltro({ ...filtro, ...{ "dataInicial": e.target.value } })}
                />
              </FinField>
              <FinField label="Até">
                <input
                  type="date"
                  className="fin-pp__input"
                  style={{ paddingLeft: 14 }}
                  value={filtro.dataFinal || ''}
                  onChange={e => setFiltro({ ...filtro, ...{ "dataFinal": e.target.value } })}
                />
              </FinField>
              <FinField label="Cliente" grow>
                <ReactSelect
                  isClearable
                  placeholder="Todos"
                  id="cliente"
                  value={filtro.cliente ? { value: filtro.cliente, label: filtro.nomeCliente } : null}
                  onChange={e => setFiltro({ ...filtro, ...{ "cliente": e.value, "nomeCliente": e.label } })}
                  loadOptions={(input) => {
                    return api({
                      method: 'get',
                      action: '/participante',
                      token: token,
                      params: {
                        nome: input,
                        tipoCadastro: "cliente",
                        situacaoRegistro: "ativo",
                      }
                    })
                      .then(response => {
                        let options = response.data.data.map((registro) => {
                          var label = registro.codigo +
                            ' - ' + registro.nome +
                            (registro.enderecomunicipio ? ' - ' + registro.enderecomunicipio.nome + '-' + registro.enderecomunicipio.uf : '') +
                            (registro.cnpj ? ' (' + registro.cnpj + ')' : '') +
                            (registro.cpf ? ' (' + registro.cpf + ')' : '') +
                            (registro.nomefantasia ? ' - ' + registro.nomefantasia : '');
                          return {
                            value: registro.id,
                            label: label
                          };
                        });
                        return options;
                      })
                      .catch(function (error) {
                        //console.log(error);
                      });
                  }}
                />
              </FinField>
              <FinField label="Responsável" grow>
                <ReactSelect
                  placeholder="Todos"
                  // key={"responsavel-" + (filtro.cargo?.map(c => c.value).join(',') || "todos")}
                  isMulti={true}
                  isClearable={true}
                  value={filtro.filtroResponsavel ?? null}
                  onChange={e => setFiltro({ ...filtro, filtroResponsavel: e && e.length > 0 ? e : null, qualquerResponsavel: e && e.length > 0 ? e.map(r => r.value) : null })}
                  loadOptions={(input) => {
                    return api({
                      method: 'get',
                      action: '/participante',
                      token: token,
                      params: {
                        nome: input,
                        situacaoRegistro: "ativo",
                        tipoCadastro: ["responsavelcliente", "pessoa"],
                        // cargo: filtro.cargo?.map(c => c.value),
                      }
                    })
                      .then(response => {
                        let options = response.data.data.map((registro) => {
                          var label = registro.codigo +
                            ' - ' + registro.nome +
                            (registro.enderecomunicipio ? ' - ' + registro.enderecomunicipio.nome + '-' + registro.enderecomunicipio.uf : '') +
                            (registro.cnpj ? ' (' + registro.cnpj + ')' : '') +
                            (registro.cpf ? ' (' + registro.cpf + ')' : '') +
                            (registro.nomefantasia ? ' - ' + registro.nomefantasia : '');
                          return {
                            value: registro.id,
                            label: label,
                            // cargo: registro.cargo
                          };
                        });
                        return options;
                      })
                      .catch(function (error) {
                        //console.log(error);
                      });
                  }}
                />
              </FinField>
              {/* Filtro Cargo (desativado)
              <FinField label="Cargo" grow>
                <ReactSelect
                  isMulti={true}
                  placeholder="Todos"
                  value={filtro.cargo || []}
                  onChange={(e) => setFiltro({
                    ...filtro,
                    "cargo": e?.length ? e : null,
                  })}
                  isClearable={true}
                  loadOptions={(input) => {
                    return api({
                      method: 'get',
                      action: '/rhCargo',
                      token: token,
                      params: { nome: input }
                    })
                      .then(response => {
                        let options = response.data.data.map(registro => ({ value: registro.id, label: registro.nome }));
                        return options;
                      })
                      .catch(function (error) {
                        //console.log(error);
                      });
                  }}
                />
              </FinField> */}
              <FinField label="Status" grow>
                <ReactSelect
                  placeholder="Todos"
                  isMulti={true}
                  isClearable={true}
                  value={filtro.situacao ? filtro.situacao.map(v => ({ value: v, label: { ativo: 'Em andamento', fechado: 'Concluído', pendente: 'Pendente', cancelado: 'Cancelado' }[v] ?? v })) : null}
                  onChange={(e) => setFiltro({
                    ...filtro,
                    situacao: e && e.length > 0 ? e.map(i => i.value) : null,
                  })}
                  options={[
                    { value: 'ativo', label: 'Em andamento' },
                    { value: 'fechado', label: 'Concluído' },
                    { value: 'pendente', label: 'Pendente' },
                    { value: 'cancelado', label: 'Cancelado' }
                  ]}
                />
              </FinField>
              <FinField label="Buscar" grow>
                <FinInput
                  type="text"
                  placeholder="Evento, cliente..."
                  value={buscarInput}
                  onChange={e => setBuscarInput(e.target.value)}
                />
              </FinField>
            </div>
            <div className="fin-pp__actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="fin-pp__btn fin-pp__btn--sm"
                onClick={() => setFiltro({ empresa })}
              >Limpar</button>
              <button type="submit" className="fin-pp__btn fin-pp__btn--primary">
                <FaDownload size={16} /> Exportar Excel
              </button>
            </div>
          </form>
        </div>

        {/* Stat-cards */}
        <div className="row match-height" style={{ marginBottom: 4 }}>
          {STAT_CARDS.map(card => (
            <div className="col-12 col-sm-6 col-lg-3" key={card.key}>
              <div className="fin-pp__card" style={{ padding: 18, borderTop: `3px solid ${card.cor}`, marginBottom: 16 }}>
                <p className="fin-pp__cphead-title" style={{ marginBottom: 4 }}>{card.label}</p>
                <div className="fin-pp__title" style={{ fontSize: 28, lineHeight: 1.1, margin: 0 }}>
                  {isLoadingResumo ? <span style={{ fontSize: 14, color: 'var(--fin-mt)' }}>Carregando...</span> : resumo[card.key]}
                </div>
                <p className="fin-pp__cphead-sub" style={{ margin: 0 }}>{card.desc}</p>
              </div>
            </div>
          ))}
          <div className="col-12 col-sm-6 col-lg-3">
            <div className="fin-pp__card" style={{ padding: 18, borderTop: '3px solid #1a8c4a', marginBottom: 16 }}>
              <p className="fin-pp__cphead-title" style={{ marginBottom: 4 }}>Taxa de Conclusão</p>
              <div className="fin-pp__title" style={{ fontSize: 28, lineHeight: 1.1, margin: 0 }}>
                {isLoadingResumo ? <span style={{ fontSize: 14, color: 'var(--fin-mt)' }}>Carregando...</span> : `${resumo.taxaConclusao}%`}
              </div>
              <p className="fin-pp__cphead-sub" style={{ margin: 0 }}>
                {isLoadingResumo ? 'Carregando...' : `${resumo.eventosConcluidos} de ${resumo.totalEventos} eventos`}
              </p>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="row">
          <div className="col-md-6 d-md-flex">
            <div className="fin-pp__card d-flex flex-column" style={{ padding: 16, marginBottom: 16, flex: 1 }}>
              <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                <span className="fin-pp__cphead-ic"><FaChartBar size={16} /></span>
                <h2 className="fin-pp__cphead-title">Eventos por Mês</h2>
              </div>
              <div className="flex-grow-1">
                {isLoadingResumo ?
                  <div className="d-flex w-100 justify-content-center align-items-center gap-3 px-4 py-3">
                    <div className="spinner-border text-primary flex-shrink-0" role="status" style={{ width: '2rem', height: '2rem' }} />
                    <span>Carregando...</span>
                  </div>
                  :
                  <ResponsiveContainer width="100%" height={barChartHeight}>
                    <BarChart data={eventosPorMes}>
                      <XAxis
                        height={20}
                        dataKey="mes"
                        tick={{ fontSize: 10, fill: "#4E5057" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        width={ticks[ticks.length - 1].toString().length * 8 + 8}
                        ticks={ticks}
                        domain={[0, ticks[ticks.length - 1]]}
                        interval={0}
                        tick={{ fontSize: 10, fill: "#4E5057" }}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        cursor={{ fill: "#EAF0F9" }}
                        contentStyle={{
                          border: "1px solid #D4DCF0",
                          borderRadius: 8,
                          fontSize: 12,
                          color: "#0F2344",
                        }}
                        formatter={(value) => [value, "Eventos"]}
                        labelFormatter={(label) => <strong>Mês: {label}</strong>}
                      />
                      {ticks.map(v => <ReferenceLine key={v} y={v} stroke="#EDF1F9" />)}
                      <Bar dataKey="total" fill="#2E73E9" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                }
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="fin-pp__card" style={{ padding: 16, marginBottom: 16 }}>
              <div className="fin-pp__cphead" style={{ marginBottom: 12 }}>
                <span className="fin-pp__cphead-ic"><FaChartPie size={16} /></span>
                <h2 className="fin-pp__cphead-title">Distribuição por Cliente</h2>
              </div>
              <div ref={pieBodyRef}>
                {isLoadingResumo ?
                  <div className="d-flex w-100 justify-content-center align-items-center gap-3 px-4 py-3">
                    <div className="spinner-border text-primary flex-shrink-0" role="status" style={{ width: '2rem', height: '2rem' }} />
                    <span>Carregando...</span>
                  </div>
                  :
                  <>
                    <ResponsiveContainer width="100%" height={170} >
                      <PieChart margin={{ top: 0 }}>
                        <Pie
                          data={distribuicaoPorCliente}
                          innerRadius="55%"
                          outerRadius="100%"
                          dataKey="value"
                          paddingAngle={2}
                        >
                          {distribuicaoPorCliente.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const { name, value, fill } = payload[0].payload;
                            return (
                              <div className="bg-white border rounded-1 px-3 py-2" style={{ fontSize: 12 }}>
                                <strong>{name}</strong><br />
                                <span className="d-inline-block me-2" style={{ width: 10, height: 10, background: fill, borderRadius: 2 }} />
                                {value}
                              </div>
                            );
                          }}
                        />
                        {/* <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="left"
                          iconType="square"
                          iconSize={10}
                          formatter={(value, entry) => `${value} (${entry.payload.value})`}
                          wrapperStyle={{ fontSize: 11, color: "#4E5057", lineHeight: "20px" }}
                        /> */}
                      </PieChart>
                    </ResponsiveContainer>
                    <ul className="list-unstyled d-flex flex-wrap gap-2 mb-0 mt-2" style={{ fontSize: 11, color: "#4E5057" }}>
                      {distribuicaoPorCliente.map((entry, index) => (
                        <li key={index}>
                          <span className="d-inline-block me-1" style={{ borderRadius: 2, width: 10, height: 10, background: COLORS[index % COLORS.length] }} />
                          {entry.name} ({entry.value})
                        </li>
                      ))}
                    </ul>
                  </>
                }
              </div>
            </div>
          </div>
        </div>

        {/* Tabela de eventos */}
        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: 16 }}>
            <div className="fin-pp__cphead">
              <span className="fin-pp__cphead-ic"><FaTable size={16} /></span>
              <h2 className="fin-pp__cphead-title">Eventos Cadastrados no Sistema</h2>
            </div>
          </div>
          <div className="fin-pp__tblwrap">
            <table className="fin-pp__tbl" style={{ minWidth: 1100, tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: 75, textAlign: 'right' }}>ID</th>
                  <th>Nome do Evento</th>
                  <th>Cliente</th>
                  <th style={{ width: 210 }}>Tipo</th>
                  <th>Responsável</th>
                  <th style={{ width: 130 }}>Status</th>
                  <th style={{ width: 110 }}>Inserido Em</th>
                  <th style={{ width: 110 }}>Data Evento</th>
                  <th style={{ width: 70, textAlign: 'right' }}>PAX</th>
                  <th style={{ width: 120, textAlign: 'center' }}>Valor Est.</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingTabela ? (
                  <tr>
                    <td colSpan={10} className="fin-pp__empty">
                      <div className="d-flex w-100 justify-content-center align-items-center gap-3 px-4 py-3">
                        <div className="spinner-border text-primary flex-shrink-0" role="status" style={{ width: '2rem', height: '2rem' }} />
                        <span>Carregando...</span>
                      </div>
                    </td>
                  </tr>
                ) : eventos.length === 0 ? (
                  <tr><td colSpan={10} className="fin-pp__empty">Nenhum registro</td></tr>
                ) : (
                  eventos.map((row, index) => {
                    const s = statusMap[row.situacao];
                    return (
                      <tr key={row.id ?? index}>
                        <td style={{ textAlign: 'right' }}>{row.codigo ?? ""}</td>
                        <td>{row.nome ?? ""}</td>
                        <td className="fin-pp__cell-strong" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{row.cliente?.nome ?? ""}</td>
                        <td>{tiposEvento[row.tipo] ?? row.tipo ?? ""}</td>
                        <td className="fin-pp__cell-muted">{nomesResponsaveis(row)}</td>
                        <td>
                          {s
                            ? <span className="badge rounded-pill fw-normal text-nowrap" style={{ alignSelf: 'center', backgroundColor: s.bg, color: s.color }}>{s.label}</span>
                            : (row.situacao ?? "")}
                        </td>
                        <td>{row.created_at ? new Date(row.created_at).toLocaleDateString('pt-BR') : ""}</td>
                        <td>{row.data ? new Date(row.data.replace(' ', 'T')).toLocaleDateString('pt-BR') : ""}</td>
                        <td style={{ textAlign: 'right' }}>{row.quantidadePessoas != null ? Math.round(row.quantidadePessoas) : "0"}</td>
                        <td style={{ textAlign: 'center' }}>
                          {row.previsaoReceita != null
                            ? Number(row.previsaoReceita).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                            : ""}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="fin-pp__pagination">
            <div className="fin-pp__pagination-info">{pagination.total} registros</div>
            <div className="fin-pp__pagination-ctrls">
              <select
                className="fin-pp__select"
                style={{ height: 34 }}
                value={pagination.pageSize}
                onChange={(e) => buscarTabela({ page: 1, pageSize: Number(e.target.value), resetPage: true })}
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} linhas</option>)}
              </select>
              <button
                type="button"
                className="fin-pp__btn fin-pp__btn--sm"
                disabled={pagination.page <= 1}
                onClick={() => buscarTabela({ page: pagination.page - 1 })}
              >Anterior</button>
              <span className="fin-pp__pagination-info">{pagination.page} / {totalPaginas}</span>
              <button
                type="button"
                className="fin-pp__btn fin-pp__btn--sm"
                disabled={pagination.page >= totalPaginas}
                onClick={() => buscarTabela({ page: pagination.page + 1 })}
              >Próxima</button>
            </div>
          </div>
        </div>
      </div>
    </ErpTemplate>
  );
}
